/**
 * Join page - Accept invitation and create account in existing tenant
 */

import React, { useState, useEffect } from 'react';
import { useNavigate, Link as RouterLink } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Building2, Loader2 } from 'lucide-react';
import { cn } from '../../lib/utils';
import { Button } from '../../components/ui/button';
import { Card, CardContent } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Alert, AlertDescription } from '../../components/ui/alert';
import { Separator } from '../../components/ui/separator';
import { GoogleIcon } from '../../components/ui/google-icon';
import { MicrosoftIcon } from '../../components/ui/microsoft-icon';
import { AuthApiError, authApi, launchSSO, useAuth } from '../../shared/auth';
import { getExplicitLanguageChoice } from '../../i18n/useGeoLanguageDefault';
import { setSSOItem } from '../../shared/auth/ssoStorage';
import { LanguageSelector, PasswordField } from '../../shared/components';
import { ConsentCheckboxes } from '../../shared/components/auth/ConsentCheckboxes';
import { usePasswordHint } from '../../shared/hooks/usePasswordHint';
import { validatePassword } from '../../shared/utils/validation';
import { getOAuthRedirectUri, isCustomDomain, getCurrentDomain, getNativePlatform } from '../../shared/utils';
import { invitationApi } from '../../modules/admin/services/invitationApi';
import { getGenericErrorMessage } from "@/shared/utils/errors";
import { PAGE_TITLE_COMPACT } from '../../shared/styles/typography';
import { eraseFragmentToken, readFragmentToken } from '../../shared/api/publicToken';

export function JoinPage() {
  const navigate = useNavigate();
  const { t } = useTranslation('auth');
  const passwordHint = usePasswordHint();
  const { establishSessionForNewIdentity } = useAuth();

  // An invitation token creates an account inside somebody else's tenant, so it
  // arrives in the URL FRAGMENT — never transmitted, never in an access log.
  // Read once as a pure lazy initializer (StrictMode calls it twice); erased in
  // its own effect below. No `?token=` fallback: an already-sent link of the old
  // shape fails CLOSED onto the invalid-invitation panel.
  const [token] = useState(readFragmentToken);

  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    password: '',
    confirmPassword: '',
  });
  const [error, setError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [isLoading, setIsLoading] = useState(false);
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [contentProcessingConsent, setContentProcessingConsent] = useState(false);
  const [ssoLoadingProvider, setSSOLoadingProvider] = useState<string | null>(null);
  const isSSOLoading = ssoLoadingProvider !== null;
  const [isVerifying, setIsVerifying] = useState(true);
  const [invitationData, setInvitationData] = useState<{
    valid: boolean;
    email: string;
    tenant_id: string;
    tenant_name: string;
    role: string;
    invited_by_name: string;
  } | null>(null);

  useEffect(() => {
    eraseFragmentToken();
  }, []);

  // Verify invitation token on mount
  useEffect(() => {
    if (!token) {
      setError(t('join.invalidToken'));
      setIsVerifying(false);
      return;
    }

    invitationApi.verifyInvitation(token)
      .then((data) => {
        if (data.valid) {
          setInvitationData(data);
        } else {
          setError(t('join.invalidToken'));
        }
      })
      .catch(() => {
        setError(t('join.invalidToken'));
      })
      .finally(() => {
        setIsVerifying(false);
      });
  }, [token, t]);

  const handleSSO = async (provider: 'google' | 'microsoft', providerName: string) => {
    if (!token || !invitationData) return;
    setError(null);
    setSSOLoadingProvider(provider);

    try {
      const redirectUri = getOAuthRedirectUri();

      if (isCustomDomain()) {
        // Cross-domain flow: OAuth callback goes to the main domain. Mint the
        // OAuth state on the MAIN domain (top-level navigation) so the backend's
        // per-flow httpOnly binding cookie lands there (login-CSRF /
        // session-fixation defense), and the invitation data rides the state
        // payload. sessionStorage set on this custom domain is unreadable at the
        // main-domain callback, so we navigate straight to the init endpoint.
        const targetDomain = getCurrentDomain();
        window.location.href = authApi.buildCrossDomainInitUrl(provider, redirectUri, targetDomain, {
          invitation_token: token,
          invitation_tenant_id: invitationData.tenant_id,
          invitation_tenant_name: invitationData.tenant_name,
        });
        return;
      }

      const nativePlatform = getNativePlatform();
      const stateExtra = nativePlatform ? { platform: nativePlatform } : undefined;
      const { auth_url, state } = await authApi.ssoInit(provider, redirectUri, stateExtra);

      setSSOItem('sso_state', state);
      setSSOItem('sso_redirect_uri', redirectUri);
      setSSOItem('sso_provider', provider);
      setSSOItem('sso_invitation_token', token);
      setSSOItem('sso_invitation_tenant_id', invitationData.tenant_id);
      setSSOItem('sso_invitation_tenant_name', invitationData.tenant_name);
      const result = await launchSSO(auth_url);
      if (result.status === 'cancelled') {
        setSSOLoadingProvider(null);
        return;
      }
      if (result.status === 'completed') {
        navigate(result.inAppPath);
        return;
      }
    } catch (err) {
      if (err instanceof AuthApiError) {
        setError(t('sso.providerError', { provider: providerName }));
      } else {
        setError(t('sso.error'));
      }
      setSSOLoadingProvider(null);
    }
  };

  const handleGoogleSSO = () => handleSSO('google', 'Google');
  const handleMicrosoftSSO = () => handleSSO('microsoft', 'Microsoft');

  const handleChange = (field: string) => (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData((prev) => ({ ...prev, [field]: e.target.value }));
    if (fieldErrors[field]) {
      setFieldErrors((prev) => {
        const next = { ...prev };
        delete next[field];
        return next;
      });
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setFieldErrors({});

    if (!token || !invitationData) {
      setError(t('join.invalidToken'));
      return;
    }

    // Validation
    const errors: Record<string, string> = {};

    if (!formData.first_name.trim()) {
      errors.first_name = t('validation.firstNameRequired');
    }

    if (!formData.last_name.trim()) {
      errors.last_name = t('validation.lastNameRequired');
    }

    const passwordValidation = validatePassword(formData.password);
    if (!passwordValidation.isValid) {
      errors.password = t(passwordValidation.errorKey!, passwordValidation.errorParams);
    }

    if (formData.password !== formData.confirmPassword) {
      errors.confirmPassword = t('resetPassword.passwordMismatch');
    }

    if (!termsAccepted) {
      errors.terms_accepted = t('consent.termsRequired');
    }
    if (!contentProcessingConsent) {
      errors.content_processing_consent = t('consent.contentProcessingRequired');
    }

    if (Object.keys(errors).length > 0) {
      setFieldErrors(errors);
      return;
    }

    setIsLoading(true);

    try {
      const response = await authApi.registerWithInvitation({
        invitation_token: token,
        first_name: formData.first_name.trim(),
        last_name: formData.last_name.trim(),
        password: formData.password,
        // Only an EXPLICIT language choice becomes the account preference —
        // the ambient UI language may be a GeoIP guess (backend defaults 'en').
        preferred_language: getExplicitLanguageChoice(),
        terms_accepted: termsAccepted,
        content_processing_consent: contentProcessingConsent,
      });

      // The invitation response carries the same full user as /login, so the
      // session is seeded straight from it. AuthContext also clears any prior
      // identity's state on this browser.
      await establishSessionForNewIdentity(response);

      navigate('/');
    } catch (err) {
      if (err instanceof AuthApiError) {
        if (err.details && Object.keys(err.details).length > 0) {
          setFieldErrors(err.details);
        } else {
          setError(err.message);
        }
      } else {
        setError(getGenericErrorMessage());
      }
    } finally {
      setIsLoading(false);
    }
  };

  if (isVerifying) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  if (!invitationData) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background p-4">
        <Card className="max-w-md w-full">
          <CardContent className="p-8 text-center">
            <h1 className={`${PAGE_TITLE_COMPACT} text-destructive mb-2`}>
              {t('join.invalidTitle')}
            </h1>
            <p className="text-muted-foreground mb-6">
              {error || t('join.invalidToken')}
            </p>
            <Button asChild>
              <RouterLink to="/login">
                {t('join.goToLogin')}
              </RouterLink>
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-background p-4">
      <Card className="max-w-md w-full">
        <CardContent className="p-8">
          <div className="flex justify-end mb-2">
            <LanguageSelector variant="labeled" />
          </div>
          {/* Organization Header */}
          <div className="flex items-center justify-center gap-2 mb-4 p-4 bg-primary/5 rounded-lg">
            <Building2 className="h-7 w-7 text-primary" />
            <h1 className={`${PAGE_TITLE_COMPACT} text-primary`}>
              {invitationData.tenant_name}
            </h1>
          </div>

          <p className="text-center mb-2">
            {t('join.subtitle', { name: invitationData.invited_by_name })}
          </p>

          <div className="flex justify-center mb-6">
            <Badge variant="outline">
              {t('join.invitedAs', { role: t(`join.roles.${invitationData.role}`) })}
            </Badge>
          </div>

          {/* SSO Buttons */}
          <div className="space-y-2 mb-4">
            <Button
              variant="outline"
              className="w-full"
              size="lg"
              onClick={handleGoogleSSO}
              disabled={isLoading || isSSOLoading}
            >
              {ssoLoadingProvider === 'google' ? (
                <Loader2 className="mr-2 h-5 w-5 animate-spin" />
              ) : (
                <GoogleIcon className="mr-2 h-5 w-5" />
              )}
              {t('sso.continueWith', { provider: 'Google' })}
            </Button>
            <Button
              variant="outline"
              className="w-full"
              size="lg"
              onClick={handleMicrosoftSSO}
              disabled={isLoading || isSSOLoading}
            >
              {ssoLoadingProvider === 'microsoft' ? (
                <Loader2 className="mr-2 h-5 w-5 animate-spin" />
              ) : (
                <MicrosoftIcon className="mr-2 h-5 w-5" />
              )}
              {t('sso.continueWith', { provider: 'Microsoft' })}
            </Button>
          </div>

          <div className="relative my-6">
            <Separator />
            <span className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 bg-card px-2 text-sm text-muted-foreground">
              {t('sso.or')}
            </span>
          </div>

          {error && (
            <Alert variant="destructive" className="mb-4">
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            {/* Email (read-only) */}
            <div className="space-y-2">
              <Label htmlFor="email">{t('register.email')}</Label>
              <Input
                id="email"
                value={invitationData.email}
                disabled
                readOnly
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="first_name">{t('register.firstName')}</Label>
                <Input
                  id="first_name"
                  value={formData.first_name}
                  onChange={handleChange('first_name')}
                  className={cn(fieldErrors.first_name && 'border-destructive')}
                  disabled={isLoading || isSSOLoading}
                  autoFocus
                />
                {fieldErrors.first_name && (
                  <p className="text-sm text-destructive">{fieldErrors.first_name}</p>
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="last_name">{t('register.lastName')}</Label>
                <Input
                  id="last_name"
                  value={formData.last_name}
                  onChange={handleChange('last_name')}
                  className={cn(fieldErrors.last_name && 'border-destructive')}
                  disabled={isLoading || isSSOLoading}
                />
                {fieldErrors.last_name && (
                  <p className="text-sm text-destructive">{fieldErrors.last_name}</p>
                )}
              </div>
            </div>

            <PasswordField
              label={t('register.password')}
              value={formData.password}
              onChange={handleChange('password')}
              error={!!fieldErrors.password}
              helperText={fieldErrors.password || passwordHint}
              disabled={isLoading || isSSOLoading}
              autoComplete="new-password"
            />

            <PasswordField
              label={t('register.confirmPassword')}
              value={formData.confirmPassword}
              // eslint-disable-next-line i18next/no-literal-string -- internal form field key, not user-facing copy
              onChange={handleChange('confirmPassword')}
              error={!!fieldErrors.confirmPassword}
              helperText={fieldErrors.confirmPassword}
              disabled={isLoading || isSSOLoading}
              autoComplete="new-password"
            />

            <ConsentCheckboxes
              termsAccepted={termsAccepted}
              contentProcessingConsent={contentProcessingConsent}
              onTermsChange={setTermsAccepted}
              onContentProcessingChange={setContentProcessingConsent}
              disabled={isLoading || isSSOLoading}
              termsError={fieldErrors.terms_accepted}
              contentProcessingError={fieldErrors.content_processing_consent}
            />

            <Button
              type="submit"
              className="w-full"
              size="lg"
              disabled={isLoading || isSSOLoading || !termsAccepted || !contentProcessingConsent}
            >
              {isLoading ? (
                <Loader2 className="h-5 w-5 animate-spin" />
              ) : (
                t('join.submit')
              )}
            </Button>

            <p className="text-sm text-center text-muted-foreground">
              {t('register.hasAccount')}{' '}
              <RouterLink to="/login" className="text-primary hover:underline">
                {t('register.signIn')}
              </RouterLink>
            </p>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
